<template>
  <main
    class="page"
    role="main"
    aria-labelledby="page-title"
  >
    <section
      class="card p-3"
      style="margin-top: 12px;"
    >
      <form
        class="form"
        aria-label="Filter"
        @submit.prevent
      >
        <div class="field">
          <label for="schoolYearSelect">Schuljahr</label>
          <select
            id="schoolYearSelect"
            v-model="schoolYearCode"
            class="input"
            :disabled="loading"
            @change="onSchoolYearChanged"
          >
            <option :value="''">
              Bitte wählen …
            </option>

            <optgroup
              v-for="g in schoolYearGroups"
              :key="g.label"
              :label="g.label"
            >
              <option
                v-for="y in g.items"
                :key="y.code"
                :value="y.code"
              >
                {{ y.code }}
              </option>
            </optgroup>
          </select>
        </div>

        <div class="field">
          <label for="studentSearch">Schülersuche</label>
          <input
            id="studentSearch"
            v-model="search"
            type="search"
            class="input"
            placeholder="Name oder ID eingeben …"
            autocomplete="off"
            :disabled="loading"
            @keydown.enter.prevent
            @keyup="onSearchKeyup"
          >
        </div>

        <div class="field">
          <label for="classSelect">Klasse</label>
          <select
            id="classSelect"
            v-model="classId"
            class="input"
            :disabled="loading"
            @change="onClassChanged"
          >
            <option :value="null">
              Alle Klassen
            </option>
            <option
              v-for="opt in classOptions"
              :key="opt.id"
              :value="opt.id"
            >
              {{ opt.name }}
            </option>
          </select>
        </div>

        <div class="field actions-field">
          <button
            type="button"
            class="btn"
            :disabled="loading"
            @click="resetFilters"
          >
            Reset
          </button>
          <button
            type="button"
            class="btn ml-4"
            :disabled="loading || filteredRows.length === 0"
            title="Tabellenergebnisse als PDF exportieren"
            style="background-color: lightgoldenrodyellow"
            @click="exportPdf"
          >
            <i
              class="pi pi-file-pdf"
              aria-hidden="true"
            />
            PDF
          </button>
        </div>

        <p
          v-if="error"
          class="notice notice-danger mt-12"
          role="alert"
        >
          {{ error }}
        </p>
        <p
          v-if="!error && !loading && rows.length === 0"
          class="notice mt-12"
          role="status"
        >
          Keine Daten für den gewählten Zeitraum gefunden.
        </p>
      </form>
    </section>

    <section
      v-if="rows.length > 0"
      class="card"
      style="margin-top: 12px;"
    >
      <div style="overflow-x:auto;">
        <table style="width:100%; border-collapse: collapse;">
          <thead>
            <tr
              style="text-align: left; font-size: 12px; color: var(--muted);"
            >
              <th style="padding: 8px; border-bottom: 1px solid var(--border);">
                Schüler
              </th>
              <th
                class="text-center"
                style="padding: 8px; border-bottom: 1px solid var(--border); line-height: 14px;"
              >
                Tage
                <span class="font-light d-block">mit Angebot</span>
              </th>
              <th style="padding: 8px; border-bottom: 1px solid var(--border);">
                Fehltage
              </th>
              <th style="padding: 8px; border-bottom: 1px solid var(--border);">
                Fehlzeiten
                <span class="font-light d-block">absolut / Lernzeiten</span>
              </th>
              <th
                class="text-center"
                style="padding: 8px; border-bottom: 1px solid var(--border); line-height: 14px;"
              >
                Anwesenheit
                <span class="font-light d-block">Pflichtangebote / Verspätungen</span>
              </th>
              <th
                style="padding: 8px; border-bottom: 1px solid var(--border); text-align:right; width:96px;"
              >
                Aktion
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="r in pagedRows"
              :key="r.studentId"
              style="border-bottom: 1px solid var(--border);"
            >
              <td>
                {{ r.studentName }}
              </td>
              <td class="text-center">
                {{ r.offerDays }}
              </td>
              <td>
                <span
                  title="Entschuldigt"
                  style="display:inline-flex; align-items:center; gap:6px; margin-right:10px;"
                >
                  <i
                    class="pi pi-check-circle"
                    style="color: var(--success);"
                  />
                  {{ r.excusedDays }}
                </span>
                <span
                  title="Unentschuldigt"
                  style="display:inline-flex; align-items:center; gap:6px;"
                >
                  <i
                    class="pi pi-times-circle"
                    style="color: var(--danger);"
                  />
                  {{ r.unexcusedDays }}
                </span>
                <span
                  title="Gesamt Fehltage"
                  style="display:inline-flex; align-items:center; gap:6px; margin-left:10px;"
                >
                  <span class="muted text-small pl-1">
                    ({{ r.totalAbsenceDays }}
                    | {{ formatPercent(r.totalAbsenceDays / Math.max(r.offerDays, 1)) }})</span>
                </span>
              </td>
              <td>
                <span
                  title="Entschuldigte Fehlstunden"
                  style="display:inline-flex; align-items:center; gap:6px; margin-right:10px;"
                >
                  <i
                    class="pi pi-check-circle"
                    style="color: var(--success);"
                  />
                  {{ r.excusedHours > 0 ? formatHours(r.excusedHours) : r.excusedHours }}
                </span>
                <span
                  title="Unentschuldigte Fehlstunden"
                  style="display:inline-flex; align-items:center; gap:6px;"
                >
                  <i
                    class="pi pi-times-circle"
                    style="color: var(--danger);"
                  />
                  {{ r.unexcusedHours > 0 ? formatHours(r.unexcusedHours) : r.unexcusedHours }}
                </span>
                <span
                  class="muted text-small pl-3"
                  title="Fehlzeiten in Lernzeiten (90 Minuten je Einheit)"
                  style="display:inline-flex; align-items:center; gap:6px;"
                >
                  ({{ formatLernzeitUnits(r.excusedHours) }} | {{ formatLernzeitUnits(r.unexcusedHours) }})
                </span>
              </td>
              <td class="text-center">
                <span
                  style="display:inline-flex; gap:5px; align-items:center; "
                >
                  <span
                    title="Anwesenheit in Pflichtangeboten"
                  >
                    {{ formatPercent((Number(r.attendancePercent ?? 0)) / 100) }}
                  </span>
                  <span
                    style="font-size: 12px;"
                    title="Verspätungen im gewählten Zeitraum"
                  >
                    ({{ Number(r.tardinessCount ?? 0) }})
                  </span>
                </span>
              </td>
              <td class="text-right">
                <button
                  class="icon-btn mr-2"
                  type="button"
                  :aria-label="`Details zu ${r.studentName}`"
                  @click="openDetails(r)"
                >
                  <i class="pi pi-search" />
                </button>
                <button
                  class="icon-btn"
                  type="button"
                  :aria-label="`Diagramm zu ${r.studentName}`"
                  @click="openChart(r)"
                >
                  <i class="pi pi-chart-bar" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination (ähnlich PasswordResetsCard.vue, hier clientseitig) -->
      <div
        class="mt-12"
        style="display:flex;align-items:center;gap:8px;justify-content:flex-end;"
      >
        <span
          class="muted"
          style="margin-right:auto"
        >
          Gesamt: {{ totalRows }}
        </span>

        <label
          class="muted"
          for="absence-page-size"
        >
          pro Seite:
        </label>
        <select
          id="absence-page-size"
          v-model.number="pageSize"
          :disabled="loading"
        >
          <option
            v-for="n in [10,20,50,100]"
            :key="n"
            :value="n"
          >
            {{ n }}
          </option>
        </select>

        <button
          class="btn"
          :disabled="page <= 1 || loading"
          @click="goPrev"
        >
          Zurück
        </button>

        <span class="muted">
          Seite {{ page }} / {{ lastPage }}
        </span>

        <button
          class="btn"
          :disabled="page >= lastPage || loading"
          @click="goNext"
        >
          Weiter
        </button>
      </div>
    </section>

    <AbsenceDetailsDialog
      v-if="selectedStudent"
      :visible="showDetailsDialog"
      :student-id="selectedStudent.studentId"
      :student-name="selectedStudent.studentName"
      :from="from"
      :to="to"
      :school-year-code="schoolYearCode"
      @close="showDetailsDialog = false"
    />

    <AbsenceChartDialog
      v-if="selectedStudent"
      :visible="showChartDialog"
      :student-id="selectedStudent.studentId"
      :student-name="selectedStudent.studentName"
      :from="from"
      :to="to"
      :school-year-code="schoolYearCode"
      @close="showChartDialog = false"
    />
  </main>
</template>

<script setup>
import {ref, onMounted, onUnmounted, computed, watch} from 'vue';
import api from '@/services/api';
import AbsenceDetailsDialog from '@/views/analytics/dialogs/AbsenceDetailsDialog.vue';
import AbsenceChartDialog from '@/views/analytics/dialogs/AbsenceChartDialog.vue';
import {jsPDF} from 'jspdf';
import autoTable from 'jspdf-autotable';

// Filter-States
const search = ref('');
const classId = ref(null);

const schoolYearCode = ref('');
const defaultSchoolYearCode = ref('');
const from = ref(''); // bleibt für Dialog-Props/PDF-Text (wird aus Schuljahr gesetzt)
const to = ref('');   // bleibt für Dialog-Props/PDF-Text (wird aus Schuljahr gesetzt)
const schoolYearGroups = ref([]);

const loading = ref(false);
const error = ref('');
const rows = ref([]);

const classOptions = ref([]);

// Dialog-States
const showDetailsDialog = ref(false);
const showChartDialog = ref(false);
const selectedStudent = ref(null);

// Pagination (clientseitig, wie Muster in PasswordResetsCard.vue)
const page = ref(1);
const pageSize = ref(100);

// Clientseitiger Klassenfilter (Fallback, falls Backend nicht filtert)
const filteredRows = computed(() => {
  const cls = classId.value != null ? Number(classId.value) : null;
  if (cls == null || Number.isNaN(cls)) return rows.value;
  return rows.value.filter(r => Number(r.classLevel ?? r.class_level ?? NaN) === cls);
});

const totalRows = computed(() => filteredRows.value.length);
const lastPage = computed(() =>
    Math.max(1, Math.ceil(totalRows.value / pageSize.value))
);
const pagedRows = computed(() => {
  const start = (page.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredRows.value.slice(start, end);
});



// Schuljahre laden + Default setzen (aktuelles Schuljahr)
async function preloadSchoolYears() {
  try {
    // MV-first: nur Schuljahre anbieten, die in der Analytics-MV vorkommen
    const {data} = await api.get('/reports/absence-days/school-years');

    const groups = Array.isArray(data?.groups) ? data.groups : [];
    const normalized = groups
        .map((g) => {
          const label = String(g?.label ?? '').trim();
          const itemsRaw = Array.isArray(g?.items) ? g.items : [];
          const items = itemsRaw
              .map((it) => {
                const code = String(it?.code ?? '').trim();
                const from = String(it?.from ?? '').trim();
                const to = String(it?.to ?? '').trim();
                return code ? { code, from, to } : null;
              })
              .filter(Boolean);

          if (!label || items.length === 0) return null;
          return {label, items};
        })
        .filter(Boolean);

    schoolYearGroups.value = normalized;

    // Default: erstes Item der ersten Gruppe
    const first = schoolYearGroups.value?.[0]?.items?.[0]?.code ?? '';
    defaultSchoolYearCode.value = String(first).trim();
  } catch {
    schoolYearGroups.value = [];
    defaultSchoolYearCode.value = '';
  } finally {
    schoolYearCode.value = defaultSchoolYearCode.value;
    applySchoolYearRangeToDates();
  }
}

function applySchoolYearRangeToDates() {
  const code = (schoolYearCode.value || '').trim();

  let f = '';
  let t = '';

  if (code) {
    outer: for (const g of (Array.isArray(schoolYearGroups.value) ? schoolYearGroups.value : [])) {
      const items = Array.isArray(g?.items) ? g.items : [];
      for (const it of items) {
        const c = String(it?.code ?? '').trim();
        if (c === code) {
          f = String(it?.from ?? '').trim();
          t = String(it?.to ?? '').trim();
          break outer;
        }
      }
    }
  }

  from.value = f;
  to.value = t;
}

function formatPercent(n) {
  const v = Number.isFinite(n) ? n * 100 : 0;
  return `${Math.round(v)}%`;
}

function formatHours(value) {
  const v = Number(value) || 0;
  if (v <= 0) return '0';
  let hours = Math.floor(v);
  let minutes = Math.round((v - hours) * 60);
  if (minutes === 60) {
    hours += 1;
    minutes = 0;
  }
  const mm = String(minutes).padStart(2, '0');
  return `${hours}:${mm}`;
}

function formatLernzeitUnits(hours) {
  const h = Number(hours) || 0;
  // Umrechnung: Stunden -> Minuten -> 90-Minuten-Einheiten
  const units = (h * 60) / 90;
  const rounded = Math.round(units * 10) / 10;
  return rounded.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function openDetails(row) {
  selectedStudent.value = row;
  showDetailsDialog.value = true;
}

function openChart(row) {
  selectedStudent.value = row;
  showChartDialog.value = true;
}

// Debouncer für die Schülersuche
let searchKeyupTimer = null;

// Trigger bei Auswahl/Änderung der Filter
function onSchoolYearChanged() {
  applySchoolYearRangeToDates();
  page.value = 1;
  fetchData(true);
}

function onClassChanged() {
  page.value = 1;
  fetchData(true);
}

// Keyup-Handler (debounced), verhindert zu häufige Requests
function onSearchKeyup() {
  if (searchKeyupTimer) clearTimeout(searchKeyupTimer);
  searchKeyupTimer = setTimeout(() => {
    onSearchChanged();
  }, 400);
}

function onSearchChanged() {
  page.value = 1;
  fetchData(true);
}

function resetFilters() {
  search.value = '';
  classId.value = null;
  schoolYearCode.value = defaultSchoolYearCode.value;
  applySchoolYearRangeToDates();
  page.value = 1;
  fetchData();
}

function exportPdf() {
  try {
    const doc = new jsPDF({unit: 'pt', format: 'a4', orientation: 'landscape'});
    const ml = 40;
    let y = 40;

    // Header
    doc.setFontSize(14);
    doc.text('Abwesenheiten – Übersicht', ml, y);
    doc.setFontSize(10);
    doc.setTextColor(100);
    y += 18;

    const fmtDate = (v) => v ? new Date(v).toLocaleDateString('de-DE') : '—';
    doc.text(`Zeitraum: ${fmtDate(from.value)} – ${fmtDate(to.value)}`, ml, y);
    y += 16;

    const clsId = classId.value != null ? Number(classId.value) : null;
    const cls = (clsId != null && !Number.isNaN(clsId)) ? classOptions.value.find(c => Number(c.id) === clsId) : null;
    const classLabel = cls ? `${cls.name}. Klasse` : 'Alle Klassen';
    const searchLabel = (search.value || '').trim() ? `Suche: "${(search.value || '').trim()}"` : 'Suche: —';
    doc.text(`Filter: ${classLabel} | ${searchLabel}`, ml, y);
    y += 16;
    doc.text('E = entschuldigt / U = unentschuldigt', ml, y);
    doc.setTextColor(0);

    // Tabelle
    const head = [['Schüler', 'Tage (mit Angebot)', 'Fehltage (E/U)', 'Fehlstunden (E/U)', 'Anwesenheit / Verspätungen']];

    const body = filteredRows.value.map((r) => {
      const excDays = Number(r.excusedDays ?? 0);
      const unexcDays = Number(r.unexcusedDays ?? 0);
      const totalDays = Number(r.totalAbsenceDays ?? (excDays + unexcDays));

      const excHours = Number(r.excusedHours ?? 0);
      const unexcHours = Number(r.unexcusedHours ?? 0);
      const totalHours = excHours + unexcHours;

      const mandatoryPercent = Number(r.attendancePercent ?? 0);
      const tardinessCount = Number(r.tardinessCount ?? 0);

      return {
        schueler: String(r.studentName ?? r.studentId ?? '—'),
        tage: String(r.offerDays ?? 0),
        fehltage: {totalDays, excDays, unexcDays}, // hier als Objekt
        fehlstunden: {totalHours, excHours, unexcHours}, // Summe + Anteile
        anwesenheit: {
          mandatoryPercent,
          tardinessCount
        },
      };
    });

    autoTable(doc, {
      head,
      body,
      // dataKey-Zuweisung passend zur Reihenfolge der Spalten
      columns: [
        {header: 'Schüler', dataKey: 'schueler'},
        {header: 'Tage (mit Angebot)', dataKey: 'tage'},
        {header: 'Fehltage', dataKey: 'fehltage'},
        {header: 'Fehlstunden (E/U)', dataKey: 'fehlstunden'},
        {header: 'Anwesenheit / Verspätungen', dataKey: 'anwesenheit'},
      ],
      startY: y + 8,
      styles: {fontSize: 10, cellPadding: 4, overflow: 'linebreak'},
      headStyles: {fillColor: [204, 204, 204], textColor: 0},
      theme: 'striped',

      didParseCell(data) {
        // Standard-Textausgabe für die Spalten "fehltage", "fehlstunden" und "anwesenheit" unterdrücken
        if (
            data.section === 'body' &&
            (data.column.dataKey === 'fehltage'
                || data.column.dataKey === 'fehlstunden'
                || data.column.dataKey === 'anwesenheit')
        ) {
          data.cell.text = [''];
        }
      },

      didDrawCell(data) {
        if (data.section !== 'body') return;

        const paddingLeft = 4; // kleiner Innenabstand links
        const x = data.cell.x + paddingLeft;
        const y = data.cell.y + data.cell.height / 2;

        if (data.column.dataKey === 'fehltage') {
          const doc = data.doc;
          const raw = data.cell.raw; // { totalDays, excDays, unexcDays }
          if (!raw) return;

          const {totalDays, excDays, unexcDays} = raw;
          const totalStr = String(totalDays);
          const restStr = ` (${excDays} / ${unexcDays})`;

          doc.setFont(undefined, 'bold');
          doc.text(totalStr, x, y, {baseline: 'middle'});

          const w = doc.getTextWidth(totalStr) + 5;

          doc.setFont(undefined, 'normal');
          doc.text(restStr, x + w, y, {baseline: 'middle'});
        } else if (data.column.dataKey === 'fehlstunden') {
          const doc = data.doc;
          const raw = data.cell.raw; // { totalHours, excHours, unexcHours }
          if (!raw) return;

          const {totalHours, excHours, unexcHours} = raw;
          const totalStr = formatHours(totalHours);
          const restStr = ` (${formatHours(excHours)} / ${formatHours(unexcHours)})`;
          const lzStr = ` (${formatLernzeitUnits(excHours)} | ${formatLernzeitUnits(unexcHours)})`;

          doc.setFont(undefined, 'bold');
          doc.text(totalStr, x, y, {baseline: 'middle'});

          const w = doc.getTextWidth(totalStr) + 5;

          doc.setFont(undefined, 'normal');
          doc.text(restStr, x + w, y, {baseline: 'middle'});

          const w2 = doc.getTextWidth(restStr) + 5;
          doc.text(lzStr, x + w + w2, y, {baseline: 'middle'});
        } else if (data.column.dataKey === 'anwesenheit') {
          const doc = data.doc;
          const raw = data.cell.raw; // { mandatoryPercent, tardinessCount }
          if (!raw) return;

          const mandatory = Number(raw.mandatoryPercent ?? 0);
          const tardinessCount = Number(raw.tardinessCount ?? 0);

          const mandatoryStr = `${Math.round(mandatory)}%`;
          const text = `${mandatoryStr} (${tardinessCount})`;

          doc.setFont(undefined, 'normal');
          doc.text(text, x, y, {baseline: 'middle'});
        }
      },

      columnStyles: {
        0: {cellWidth: 'auto'},
        1: {cellWidth: 120, halign: 'center'},
        2: {cellWidth: 160},
        3: {cellWidth: 160},
        4: {cellWidth: 160, halign: 'center'}
      },
      margin: {left: ml, right: 40}
    });

    const fileSafe = (s) => String(s ?? '')
        .normalize('NFKD')
        .replace(/[^\w\s.-]/g, '')
        .trim()
        .replace(/\s+/g, '_');

    const filename = `Abwesenheiten_Uebersicht_${fileSafe(from.value || 'von')}_${fileSafe(to.value || 'bis')}.pdf`;
    doc.save(filename);
  } catch (e) {
    console.error('PDF-Export fehlgeschlagen:', e);
  }
}

async function fetchData(resetPage = false) {
  if (resetPage) {
    page.value = 1;
  }
  loading.value = true;
  error.value = '';
  try {
    const params = {
      search:
          search.value && search.value.length >= 2 ? search.value : undefined,
      classId: classId.value ?? undefined,
      schoolYearCode: schoolYearCode.value || undefined,
    };

    const {data} = await api.get('/reports/absence-days', {params});
    // NEU: Sowohl altes (Array) als auch neues (Objekt) API-Format unterstützen

    if (Array.isArray(data)) {
      rows.value = data;
      classOptions.value = [];
    } else if (data && typeof data === 'object') {
      rows.value = Array.isArray(data.items) ? data.items : [];

      const rawClasses = Array.isArray(data.classes) ? data.classes : [];
      classOptions.value = rawClasses
          .map((c) => {
            const id = c.id != null ? Number(c.id) : NaN;
            const name =
                (c.name ?? c.label ?? '').toString().trim() ||
                (Number.isFinite(id) ? `Klasse ${id}` : '');
            if (!Number.isFinite(id) || id <= 0 || !name) {
              return null;
            }
            return {id, name};
          })
          .filter(Boolean);
    } else {
      rows.value = [];
      classOptions.value = [];
    }
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || 'Laden fehlgeschlagen.';
    rows.value = [];
    classOptions.value = [];
    page.value = 1;
  } finally {
    loading.value = false;
  }
}

function goPrev() {
  if (page.value > 1) {
    page.value -= 1;
  }
}

function goNext() {
  if (page.value < lastPage.value) {
    page.value += 1;
  }
}

watch(schoolYearCode, () => {
  applySchoolYearRangeToDates();
  page.value = 1;
});

watch(pageSize, () => {
  page.value = 1;
});

watch(classId, () => {
  page.value = 1;
});

onMounted(async () => {
  await preloadSchoolYears();
  await fetchData();
});

onUnmounted(() => {
  if (searchKeyupTimer) {
    clearTimeout(searchKeyupTimer);
    searchKeyupTimer = null;
  }
});
</script>

<style scoped>
.form {
  display: grid;
  grid-template-columns: 200px 1fr 200px auto;
  gap: 12px;
  align-items: end;
}

tbody td {
  padding: 2px 6px;
}

.field {
  margin-bottom: 0;
}

@media (max-width: 1024px) {
  .form {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .form {
    grid-template-columns: 1fr;
  }
}

.input {
  height: 38px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}

.input:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.actions-field {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
}

.mt-12 {
  margin-top: 12px;
}

.muted {
  color: var(--muted);
}

.btn {
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
}

.btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-primary:hover {
  background: var(--primary-600);
  border-color: var(--primary-600);
}

.spinner {
  box-sizing: border-box;
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  margin-right: 6px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>